import axios from 'axios';
import jwtDecode from 'jwt-decode';
import FuseUtils from '@fuse/FuseUtils';
import { bootInitialCustomIntercomLauncher, getAPIHost, getExpressAPIHost, getRegion, getId, getUserRegion, resolveId } from '../../../utils';
import Split from '../../split';
import _ from 'lodash';
import { datadogRum } from '@datadog/browser-rum';
import { signOut, getCurrentSession, refreshCurrentSession } from '@milkmoovement/common_cognito_package';

// PFM-2852: only these methods may receive one refresh-first 401 retry.
const SAFE_RETRY_METHODS = new Set(['get', 'head', 'options']);

// PFM-2852: fixed telemetry contract. emitSessionRefreshEvent drops anything
// outside these enums so no caller can push arbitrary values into RUM.
const SESSION_REFRESH_REASONS = ['pre_dispatch', 'response_401'];
const SESSION_REFRESH_RESULTS = ['success', 'failure'];

/**
 * Gets the region to use for API requests.
 * Prioritizes the cached region from sessionStorage (set by fetchRegionFromBackend),
 * falls back to URL-based region detection.
 * @returns {string} The region code
 */
const getRegionForAPI = () => {
    // Check sessionStorage first, then localStorage as a fallback.
    // localStorage persists across new browser tabs/pages (same origin),
    // which is critical for Playwright tests that create new pages via
    // context.newPage() — sessionStorage is NOT inherited by new pages.
    const cachedRegion = sessionStorage.getItem('mm.region') || localStorage.getItem('mm.region');
    if (cachedRegion) {
        return cachedRegion;
    }
    return getUserRegion() || getRegion(window.location.hostname);
};

class JWTService extends FuseUtils.EventEmitter {
    // PFM-2852: in-flight single-flight slot for non-forced token freshness
    // checks, keyed by the authenticated username so an account change can
    // never hand one user's refresh promise to another user's requests.
    _ensureFreshIdTokenPromise = null;

    _ensureFreshIdTokenUser = null;

    // Last JWT attached to a request; lets us notice refreshes that happened
    // inside the Cognito package (leeway window) so telemetry stays accurate.
    _lastIssuedJwt = null;

    _getUserHost = () => {
        // Re-read region from sessionStorage before evaluating the split.
        // During init(), the region may not be cached yet (empty string),
        // causing the split to evaluate OFF → LoopBack host. After login,
        // cacheAuthenticatedRegion() sets mm.region in sessionStorage but
        // does NOT update the Split attribute. This re-sync ensures the
        // split always reflects the latest cached region.
        //
        // If region is still unknown (e.g. new browser tab with empty
        // sessionStorage, or cold start before region-config fetch
        // completes), default to Express — LoopBack is being deprecated
        // and is turned off in some environments (e.g. CDI dev).
        const region = (getRegionForAPI() || '').trim().toUpperCase();
        // Always update Split attribute to prevent stale state from
        // earlier in the SPA lifecycle (e.g. after logout clears
        // sessionStorage but not Split attributes).
        Split.setAttribute('region', region || 'OTHER');
        if (region && region !== 'OTHER') {
            return Split.getBooleanTreatment('use_express_auth_endpoints') ? getExpressAPIHost() : getAPIHost();
        }
        // Region unknown or 'OTHER' — default to Express to avoid 503 from LoopBack
        return getExpressAPIHost();
    };

    init() {
        // Ensure region is set before any split evaluation (e.g. _getUserHost)
        // to avoid cold-start routing to LoopBack when Express is intended.
        Split.setAttribute('region', getRegionForAPI());
        this.setInterceptors();
        this.handleAuthentication();
    }

    setInterceptors = () => {
        axios.interceptors.request.use(async (config) => {
            try {
                // PFM-2852: refresh before dispatch so requests never carry
                // a token that expires at the boundary (expiry 401 races).
                let jwt;
                let refreshed;
                try {
                    ({ jwt, refreshed } = await this.ensureFreshIdToken());
                } catch (refreshError) {
                    // Failure telemetry is scoped to the refresh itself; errors
                    // later in this interceptor are not refresh failures.
                    this.emitSessionRefreshEvent('pre_dispatch', 'failure');
                    throw refreshError;
                }
                if (refreshed) {
                    this.emitSessionRefreshEvent('pre_dispatch', 'success', jwt);
                }
                // eslint-disable-next-line no-param-reassign
                config.headers.Authorization = `Bearer ${jwt}`;
                // eslint-disable-next-line no-param-reassign
                config.headers.customer = getRegionForAPI();

                // PFM-2761: Scope loginas-id to LoopBack/Express API origins only.
                // loginas-id was added in PFM-2336 and set globally, causing CORS failures
                // on non-API services like metabase-signing-service.
                // loginas header stays global (pre-existing, not causing issues).
                // Use URL parsing (not substring match) so relative axios URLs like
                // '/users/me' resolve against the configured baseURL, and hostname
                // equality prevents spoofing via substring overlap.
                const requestUrl = config.url || '';
                const apiHost = getAPIHost();
                const expressHost = getExpressAPIHost();
                let isApiRequest = false;
                try {
                    const resolved = new URL(requestUrl, config.baseURL || apiHost);
                    const apiHostname = new URL(apiHost).hostname;
                    const expressHostname = new URL(expressHost).hostname;
                    isApiRequest = resolved.hostname === apiHostname || resolved.hostname === expressHostname;
                } catch (e) {
                    // If URL parsing fails, fall back to substring match (safe default:
                    // better to attach loginas-id than to silently drop it).
                    isApiRequest = requestUrl.includes(apiHost) || requestUrl.includes(expressHost);
                }
                if (isApiRequest) {
                    const loginasId = sessionStorage.getItem('selected_user');
                    if (loginasId) {
                        // eslint-disable-next-line no-param-reassign
                        config.headers['loginas-id'] = loginasId;
                    }
                }

                return config;
            } catch (e) {
                if (e.message === 'No current user') {
                    // This occurs post manual logout typically and we don't want to display a message
                    this.emit('onAutoLogout', null);
                } else {
                    this.emit('onAutoLogout', 'Token expired');
                }
                return {
                    ...config,
                    cancelToken: new axios.CancelToken((cancel) => cancel('User unauthenticated')),
                };
            }
        });
        axios.interceptors.response.use(
            (response) => response,
            async (err) => {
                if (err?.toJSON?.().message === 'Network Error' && !navigator.onLine) {
                    this.emit('onAutoLogout', 'Check your internet connection');
                }
                /* eslint-disable-next-line */
                if (!err.response || err.response.status !== 401 || !err.config) {
                    throw err;
                }

                const { config } = err;
                const method = (config.method || '').toLowerCase();
                const url = config.url || '';
                // Only auto-logout on 401 from auth-critical endpoints.
                // Data endpoint 401s (e.g. /users?filter=...) during impersonation
                // should not kill the entire session.
                const isAuthEndpoint = url.includes('/users/me') || url.includes('/users/lookup') || url.includes('/login');
                const handleAuthEndpointFailure = () => {
                    if (isAuthEndpoint && sessionStorage.getItem('areWeLocked') !== 'locked') {
                        this.emit('onAutoLogout', 'User credentials are not correct');
                    }
                    if (isAuthEndpoint) {
                        this.setSession(null);
                    }
                };

                // PFM-2852: exactly one refresh-first retry, safe reads
                // only. Writes are never automatically replayed.
                if (!config.__isRetryRequest && SAFE_RETRY_METHODS.has(method)) {
                    let jwt;
                    try {
                        ({ jwt } = await this.ensureFreshIdToken({ forceRefresh: true }));
                    } catch (refreshError) {
                        // Refresh failed: controlled logout, no stale replay.
                        this.emitSessionRefreshEvent('response_401', 'failure');
                        this.emit('onAutoLogout', 'Token expired');
                        throw err;
                    }
                    this.emitSessionRefreshEvent('response_401', 'success', jwt);
                    try {
                        return await axios({
                            ...config,
                            headers: { ...config.headers, Authorization: `Bearer ${jwt}` },
                            __isRetryRequest: true,
                        });
                    } catch (retryError) {
                        // A final 401 follows the existing handling.
                        // eslint-disable-next-line max-depth
                        if (retryError?.response?.status === 401) {
                            handleAuthEndpointFailure();
                        }
                        throw retryError;
                    }
                }

                if (!config.__isRetryRequest) {
                    handleAuthEndpointFailure();
                }
                throw err;
            }
        );
    };

    /**
     * PFM-2852: Returns an ID-token JWT that stays valid for at least 60
     * seconds, refreshing through the common Cognito package when needed.
     * `refreshed` tells callers whether a refresh occurred so telemetry
     * stays low-volume (one RUM action per refresh, not per request).
     *
     * Non-forced calls are coalesced app-side (single flight) so a burst of
     * concurrent near-expiry requests shares one refresh instead of
     * stampeding; forced calls (401 retries) always run, and the package
     * still single-flights the underlying refresh-token rotation.
     */
    ensureFreshIdToken = ({ forceRefresh = false } = {}) => {
        if (forceRefresh) {
            return this._fetchFreshIdToken(true);
        }
        const currentUser = localStorage.getItem('mm.login.currentUsername') || '';
        if (this._ensureFreshIdTokenPromise && this._ensureFreshIdTokenUser !== currentUser) {
            // Account changed while a refresh was coalescing — drop the
            // stale user's promise instead of sharing it across users.
            this._ensureFreshIdTokenPromise = null;
        }
        if (!this._ensureFreshIdTokenPromise) {
            this._ensureFreshIdTokenUser = currentUser;
            const promise = this._fetchFreshIdToken(false).finally(() => {
                // Only clear ourselves; a logout/login race may have already
                // replaced the slot with a newer user's promise.
                if (this._ensureFreshIdTokenPromise === promise) {
                    this._ensureFreshIdTokenPromise = null;
                }
            });
            this._ensureFreshIdTokenPromise = promise;
        }
        return this._ensureFreshIdTokenPromise;
    };

    _fetchFreshIdToken = async (forceRefresh) => {
        const session = await getCurrentSession({ forceRefresh, leewaySeconds: 60 });
        let jwt = session.getIdToken().getJwtToken();
        let refreshed = forceRefresh;
        if (!this.isAuthTokenValid(jwt, 60)) {
            jwt = (await refreshCurrentSession()).getIdToken().getJwtToken();
            refreshed = true;
        }
        // Detect refreshes that happened inside the Cognito package (leeway
        // window): a token different from the last one we issued means a
        // refresh occurred beneath us.
        if (!refreshed && this._lastIssuedJwt !== null && jwt !== this._lastIssuedJwt) {
            refreshed = true;
        }
        this._lastIssuedJwt = jwt;
        return { jwt, refreshed };
    };

    /**
     * PFM-2852: Token-safe RUM telemetry. Fixed enums plus a numeric
     * expiry delta only — never tokens, headers, user IDs, emails, or
     * usernames.
     */
    emitSessionRefreshEvent = (reason, result, jwt = null) => {
        try {
            // Enforce the telemetry contract: fixed enums plus a finite
            // numeric delta. Anything else is dropped, never forwarded.
            if (!SESSION_REFRESH_REASONS.includes(reason) || !SESSION_REFRESH_RESULTS.includes(result)) {
                return;
            }
            let expiryDeltaMs = 0;
            if (jwt) {
                const delta = jwtDecode(jwt).exp * 1000 - Date.now();
                expiryDeltaMs = Number.isFinite(delta) ? Math.round(delta) : 0;
            }
            datadogRum.addAction('cognito_session_refresh', { reason, result, expiryDeltaMs });
        } catch {
            // Telemetry must never break authentication flows.
        }
    };

    handleAuthentication = async () => {
        try {
            const session = await getCurrentSession();
            const accessToken = session.getIdToken().getJwtToken();

            if (!accessToken) {
                return;
            }

            if (this.isAuthTokenValid(accessToken)) {
                this.setSession(accessToken);
                this.emit('onAutoLogin', true);
            }
            // eslint-disable-next-line no-empty
        } catch {}
    };

    signInWithCognitoSession = async () => {
        const session = await getCurrentSession();
        this.setSession(session.getIdToken().getJwtToken());
        return this.fetchUserData();
    };

    setSession = (accessToken) => {
        if (accessToken) {
            axios.defaults.headers.common.Authorization = `Bearer ${accessToken}`;
            axios.defaults.headers.customer = getRegionForAPI();
            axios.defaults.crossDomain = true;
            const loginas = sessionStorage.getItem('selected_email');
            if (loginas) {
                axios.defaults.headers.common.loginas = loginas;
            } else {
                delete axios.defaults.headers.common.loginas;
            }
            return;
        }
        delete axios.defaults.headers.common.Authorization;
        delete axios.defaults.headers.common.loginas;
    };

    logout = async () => {
        this.setSession(null);
        this._dropAuthBoundaryState();
        const isLocked = sessionStorage.getItem('areWeLocked') === 'locked';
        try {
            if (!isLocked) {
                await signOut();
            }
        } finally {
            sessionStorage.clear();
            // Legacy cleanup: mm.sessionStartedAt was retired by the PFM-2849
            // idle-timeout rework; keep removing stale keys on logout.
            localStorage.removeItem('mm.sessionStartedAt');
            window.Intercom('shutdown');
            bootInitialCustomIntercomLauncher();
        }
    };

    lock = async () => {
        const clearUsername = false;
        this._dropAuthBoundaryState();
        await signOut(clearUsername);
    };

    /**
     * PFM-2852 audit hardening: logout and lock are account boundaries.
     * Never let an in-flight coalesced refresh or a cached token leak
     * across them.
     */
    _dropAuthBoundaryState = () => {
        this._ensureFreshIdTokenPromise = null;
        this._ensureFreshIdTokenUser = null;
        this._lastIssuedJwt = null;
    };

    isAuthTokenValid = (accessToken, leewaySeconds = 0) => {
        if (!accessToken) {
            return false;
        }
        const decoded = jwtDecode(accessToken);
        const currentTime = Date.now() / 1000;
        return decoded.exp >= currentTime + leewaySeconds;
    };

    fetchUserData = async () => {
        const response = await axios.get(`${this._getUserHost()}/users/me`);
        const data = resolveId(response.data);
        const { role, subuser } = data;
        if (role === 'admin') {
            sessionStorage.setItem('isAdmin', 'true');
            const impersonationAllowed = data?.permissions?.includes('CanImpersonateUsers');
            sessionStorage.setItem('impersonationAllowed', impersonationAllowed ? 'true' : 'false');
        } else {
            sessionStorage.removeItem('isAdmin');
            sessionStorage.removeItem('impersonationAllowed');
        }
        if (role === 'driver' || role === 'receiver') {
            this.setSession(null);
            const error = new Error(`${_.capitalize(role)}s please login through the driver handheld.`);
            error.name = 'RoleError';
            throw error;
        }

        if (subuser) {
            try {
                const parentName = await this.getParentName(data);
                sessionStorage.setItem('user_parent_name', parentName);
            } catch {
                sessionStorage.removeItem('user_parent_name');
            }
        }

        return {
            role,
            data,
        };
    };

    getParentName = async (userData) => {
        const parentId = getId();
        // PFM-2805: Guard against undefined ID (guest, partial rehydration, missing role mapping)
        if (!parentId) {
            return userData.name;
        }
        // if not a subuser or is a sub admin return their own name
        if (parentId === userData.id || !userData.subuser || userData.role === 'admin') {
            return userData.name;
        }

        try {
            const { data: parentResponse } = await axios.get(`${this._getUserHost()}/users/${parentId}`);
            const parentData = resolveId(parentResponse);
            return parentData.name;
        } catch {
            return userData.name;
        }
    };

    setMainUser = async (mainUser) => {
        sessionStorage.setItem('main_user_id', mainUser.id);
        const parentName = await this.getParentName(mainUser);
        sessionStorage.setItem('main_user_name', parentName);
    };

    signInAsImpersonatedUser = async (id, mainUser = null) => {
        // PFM-2805: Prevent /users/undefined requests
        if (!id) {
            throw new Error('Cannot impersonate without a user ID');
        }
        if (mainUser && !sessionStorage.getItem('main_user_id')) {
            await this.setMainUser(mainUser);
        }
        const response = await axios.get(`${this._getUserHost()}/users/${id}`);
        const data = resolveId(response.data);
        const { email, role } = data;
        axios.defaults.headers.common.loginas = email;
        sessionStorage.setItem('isImpersonating', 'true');
        sessionStorage.setItem('selected_user', data.id);
        sessionStorage.setItem('selected_name', data.name);
        sessionStorage.setItem('selected_email', email);
        return { role, data };
    };

    revertImpersonatedLogin = async () => {
        delete axios.defaults.headers.common.loginas;
        // Clear impersonation state BEFORE the API call so the request
        // interceptor does not attach loginas-id to /users/me.
        // Previously these removeItem calls were after the API call,
        // so the interceptor still found 'selected_user' in sessionStorage
        // and attached loginas-id — causing the backend to return the
        // impersonated user's data instead of the real admin's.
        sessionStorage.removeItem('isImpersonating');
        sessionStorage.removeItem('selected_user');
        sessionStorage.removeItem('selected_name');
        sessionStorage.removeItem('selected_email');
        sessionStorage.removeItem('main_user_id');
        sessionStorage.removeItem('main_user_name');

        const response = await axios.get(`${this._getUserHost()}/users/me`);
        const data = resolveId(response.data);
        const { role } = data;
        if (role === 'admin') {
            sessionStorage.setItem('isAdmin', 'true');
            const impersonationAllowed = data?.permissions?.includes('CanImpersonateUsers');
            sessionStorage.setItem('impersonationAllowed', impersonationAllowed ? 'true' : 'false');
        } else {
            sessionStorage.removeItem('isAdmin');
            sessionStorage.removeItem('impersonationAllowed');
        }
        return { role, data };
    };
}

// eslint-disable-next-line new-cap
const instance = new JWTService();

export default instance;
